import { ArrowLeft, PanelLeft } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useSidebar,
} from "@f2e/ui/components/base"

import Clock from "@/components/Clock"
import WorkspaceBadge from "@/components/WorkspaceBadge"
import type { History } from "@/types"

interface MobileAppHeaderProps {
  history?: History | null
  historyListHref?: string | null
}

const MobileAppHeader = ({
  history = null,
  historyListHref = null,
}: MobileAppHeaderProps) => {
  const { t } = useTranslation()
  const { isMobile, openMobile, toggleSidebar } = useSidebar()

  if (!isMobile) return null

  const isSidebarOpen = openMobile
  const isHistoryHeader = Boolean(historyListHref)
  const title = history?.name || ""

  return (
    <div className="flex min-h-[53px] shrink-0 items-center border-b border-border/70 bg-background px-3 py-2 md:hidden">
      <TooltipProvider delayDuration={100}>
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          {!isSidebarOpen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={toggleSidebar}
                >
                  <PanelLeft className="h-5 w-5" />
                  <span className="sr-only">
                    {t("common.headDropdown.toggleSidebar")}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {t("common.headDropdown.toggleSidebar")}
              </TooltipContent>
            </Tooltip>
          )}

          {isHistoryHeader && historyListHref && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    asChild={true}
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    <Link
                      to={historyListHref}
                      aria-label={t("historyList.detail.backToList")}
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("historyList.detail.backToList")}
                </TooltipContent>
              </Tooltip>

              <div className="min-w-0 flex-1">
                {title ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <h1 className="truncate text-base font-semibold leading-5 text-foreground">
                        {title}
                      </h1>
                    </TooltipTrigger>
                    <TooltipContent>{title}</TooltipContent>
                  </Tooltip>
                ) : (
                  <div className="h-5 w-32 rounded bg-muted" />
                )}
                {history && (
                  <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    <WorkspaceBadge workspaceProfile={history.workspace} />
                    {history.updatedAt && (
                      <>
                        <span
                          aria-hidden="true"
                          className="text-muted-foreground/50"
                        >
                          ·
                        </span>
                        <Clock
                          className="text-muted-foreground"
                          dateAt={history.updatedAt}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </TooltipProvider>
    </div>
  )
}

export default MobileAppHeader
